import { PageSection } from "@app/shared";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { HighlightedRegionSetSpecialtyGroupsWithGapsAndCount, ProviderHubServicingProvidersTableData } from "./interfaces";
import { ColumnConfig, Waffles, WafflesPaginationProps } from "@app/shared/waffles";
import { ProviderHubContext } from "./contexts/ProviderHubContext";
import ProviderBadge from "@app/shared/ProviderBadge";
import { QESButton } from "@app/shared/qes-standards";
import WaffleRowTooltip from "@app/shared/waffles/WaffleRowTooltip";
import ProviderHubService from "./providerHubService";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { ProviderNetworkType } from "@app/projects/enumerators";
import { CountyProjectContext } from "@app/projects";
import ProviderHubFilter from "./ProviderHubFilter";

const ProviderHubProvidersView: FC = () => {

  const providerHubService = useRef(new ProviderHubService()).current;
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const providerHubContext = useContext(ProviderHubContext);
  const projectContext = useContext(CountyProjectContext);
  const [loading, setLoading] = useState<boolean>(false);

  /* eslint-disable sort-keys */
  const providerHubServicingProvidersTableColumnConfig: ColumnConfig = {
    providerName: {
      displayText: "Name",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-provider-name"
    },
    identifier: {
      displayText: "Identifier",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-identifier"
    },
    networkTypes: {
      displayText: "Network Type",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-network-type",
      tooltip: "Indicates provider data source"
    },
    gapsClosed: {
      displayText: "Gap Impact", 
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-gap-impact",
      tooltip: "Provider impact may vary between counties"
    },
    specialtiesImpacted: {
      displayText: "Specialties Impacted",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-specialties-impacted"
    },
    countiesImpacted: {
      displayText: "Counties Impacted",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-counties-impacted"
    },
    accessPoints: {
      displayText: "Access Points",
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-access-points"
    },
    primaryContractEntity: {
      displayText: "Primary Contract Entity",
      hidden: !featureFlags.has(Features.PROVIDER_HUB_CONTRACT_ENTITY),
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-primary-contract-entity"
    },
    contractEntityProviders: {
      displayText: "Contract Entity Providers",
      hidden: !featureFlags.has(Features.PROVIDER_HUB_CONTRACT_ENTITY),
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-contract-entity-providers"
    },
    locationConfidence: {
      displayText: "Location Confidence",
      hidden: !projectContext.project.showLocationConfidenceScores || projectContext.runConfiguration?.showLocationConfidenceScores === false,
      sizeContentToFit: true,
      testId: "provider-hub_providers-table-location-confidence"
    }
  }

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => handlePageChange(pageNumber),
    enableBackendPagination: true,
    handlePageSizeChange: (pageSize: number) => handlePageSizeChange(pageSize),
    pageSizeOptions: [10, 100, 1000],
    recordsPerPage: providerHubContext.pageSize,
    resetToFirstPage: providerHubContext.resetToFirstPage,
    showPageSizeSelector: true,
    totalRecords: providerHubContext.providerHubServicingProviders.totalCount
  }
  /* eslint-enable sort-keys */
  
  const handleCheckboxChange = (async (selectedServicingProviderIds: string[]) => {

    providerHubContext.setSelectedServicingProviders(selectedServicingProviderIds);

    const selectedServicingProviderIdsAsNumbers = selectedServicingProviderIds.map(Number);

    // Array to store the highlighted rows in the gap table with their corresponding count, to easy reuse it in the ProviderHubGapsViewComponent
    const highlightedRegionSetSpecialtyGroupsWithGapsAndCount: HighlightedRegionSetSpecialtyGroupsWithGapsAndCount[] = [];

    providerHubContext.regionSetSpecialtyGroupsWithGaps.forEach(gap => {

      const matchedProviders = gap.servicingProviderIdsThatCloseGaps.filter((provider) => 
        selectedServicingProviderIdsAsNumbers.includes(provider)
      );

      if (matchedProviders.length > 0 ){
        highlightedRegionSetSpecialtyGroupsWithGapsAndCount.push(
          {
            count: matchedProviders.length,
            regionSetSpecialtyGroupId: gap.regionSetSpecialtyGroupId
          }
        );
      }

    });

    providerHubContext.setHighlightedRegionSetSpecialtyGroupsWithGapsAndCount(highlightedRegionSetSpecialtyGroupsWithGapsAndCount)

  });

  const handlePageSizeChange = (pageSize: number) => {
    providerHubContext.setPageSize(pageSize);
    providerHubContext.setPageIndex(1);
  }

  const handlePageChange = async (newPage: number) => {
    if (!providerHubContext.resetToFirstPage) {
      providerHubContext.setPageIndex(newPage);
    }
    if (providerHubContext.resetToFirstPage) {
      providerHubContext.setResetToFirstPage(false);
    }
  };

  const handleProviderRowExpansion = async (servicingProviderId: string): Promise<void> => {

    const accessPointData = await providerHubService.getAccessPointsByServicingProviders(Number(servicingProviderId));

    let servicingProviderAccessPointData: ProviderHubServicingProvidersTableData[] = [];

    /* eslint-disable sort-keys */
    servicingProviderAccessPointData = accessPointData.map((item) => {
      return {
        key: item.accessPointId.toString(),
        providerName: '',
        identifier: '',
        networkTypes: <ProviderBadge providerNetworkType={item.networkType}/>,
        gapsClosed: item.networkType != ProviderNetworkType.Network ?  `Closes ${item.gapsClosed == 1 ? '1 gap' : `${item.gapsClosed} gaps`}` : '---',
        specialtiesImpacted: item.specialtyImpacted,
        countiesImpacted: <WaffleRowTooltip containerClassName="provider-hub-waffle-tooltip" elements = {
          item.countiesImpacted.map(({countyName, stateCode}) => 
            `${countyName}, ${stateCode}`
          )
        } />,
        accessPoints: `${item.address} ${item.address2 ? `- ${item.address2}` : ''} - ${item.city}, ${item.state} ${item.zip}`,
        primaryContractEntity: '',
        contractEntityProviders: null as number,
        locationConfidence: item.locationConfidence.toString()
      }
    });
    /* eslint-enable sort-keys */

    providerHubContext.setProviderHubServicingProviderAccessPointData(prevMap => {

      const updatedServicingProviderAccessPointData = new Map<string, ProviderHubServicingProvidersTableData[]>();

      prevMap.forEach((item, key) => {
        updatedServicingProviderAccessPointData.set(key, key === servicingProviderId ? [...servicingProviderAccessPointData] : item)
      });

      return updatedServicingProviderAccessPointData;

     });

  }

  useEffect(() => {
    refreshData();
  }, [providerHubContext.pageIndex, providerHubContext.pageSize]);

  const refreshData = async () => {
    setLoading(true);
    const refreshedData = await providerHubService.getProviderHubServicingProviders(providerHubContext.pageIndex, providerHubContext.pageSize, providerHubContext.providersFilters);
    providerHubContext.setProviderHubServicingProviders(refreshedData);
    setLoading(false);
  }

 /* eslint-disable sort-keys */
  const providersTableData: ProviderHubServicingProvidersTableData[] = useMemo<ProviderHubServicingProvidersTableData[]>(() => {

    let data: ProviderHubServicingProvidersTableData[] = [];

    data = providerHubContext.providerHubServicingProviders.items.map((provider) => {
      return {
        key: provider.servicingProviderId.toString(),
        providerName: provider.providerName,
        identifier: provider.npi,
        networkTypes: (
          <>
            {provider.networkTypes.map((networkType, index) =>
              <ProviderBadge key={index} providerNetworkType={networkType}/>
            )}
          </>
        ),
        gapsClosed: `Closes ${provider.gapsClosed == 1 ? '1 gap' : `${provider.gapsClosed} gaps`}`,  
        specialtiesImpacted: <WaffleRowTooltip containerClassName="provider-hub-waffle-tooltip" elements={provider.specialtiesImpacted.map(item => item.name)} />,
        countiesImpacted: <WaffleRowTooltip containerClassName="provider-hub-waffle-tooltip" elements = {
          provider.countiesImpacted.map(({countyName, stateCode}) => 
            `${countyName}, ${stateCode}`
          )
        } />,
        accessPoints: provider.accessPointCount.toString(),
        primaryContractEntity:  provider.primaryContractEntity ,
        contractEntityProviders: provider.contractEntityProviders,
        locationConfidence: ''
      }
    });

    return data;

  }, [providerHubContext.providerHubServicingProviders]);
  /* eslint-enable sort-keys */

  // We assign here an empty map to be used as the initial expansion data for the providers table
  useEffect(() => {
    const emptyServicingProviderAccessPointData = new Map<string, ProviderHubServicingProvidersTableData[]>();
    providersTableData.forEach((item) => emptyServicingProviderAccessPointData.set(item.key, []));
    providerHubContext.setProviderHubServicingProviderAccessPointData(emptyServicingProviderAccessPointData);
  }, [providersTableData]);

  return (

    <>
      <div className="provider_hub__provider-table-actions-container">

        <div className="provider_hub__provider-table-actions-left">
          <ProviderHubFilter/>
          <QESButton
            className="provider-hub__providers-columns-button"
            qesType="primary"
            qesTestId="provider-hub__providers-columns-button"
            onClick={()=> {}}>
            Columns
          </QESButton>
          <label className="provider_hub__providers-table-selection-indicator">{providerHubContext.selectedServicingProviders.length} of {providerHubContext.providerHubServicingProviders.totalCount} providers selected </label>
        </div>

        <div className="provider_hub__provider-table-actions-rigth">
          <QESButton
            disabled={true}
            qesType="primary"
            qesTestId="provider-hub__providers-download-list-button"
            onClick={()=> {}}>
            Download List
          </QESButton>
          <QESButton
            disabled={true}
            qesType="primary"
            qesTestId="provider-hub__providers-compare-button"
            onClick={()=> {}}>
            Compare
          </QESButton>
          <QESButton
            disabled={true}
            qesType="primary"
            qesTestId="provider-hub__providers-target-button"
            onClick={()=> {}}>
            Target
          </QESButton>
        </div>
          
      </div>

      <div className="provider_hub__provider-table-container">
        <PageSection>

          <Waffles<ProviderHubServicingProvidersTableData>
            columns={providerHubServicingProvidersTableColumnConfig}
            data={providersTableData}
            dataExpansions={providerHubContext.providerHubServicingProviderAccessPointData}
            dataExpansionsDefaultOpen={false}
            dataExpansionOnToggleClick={handleProviderRowExpansion}
            enablePagination={true}
            fullWidth={true}
            handleCheckboxChange={handleCheckboxChange}
            highlightedRows={providerHubContext.highlightedServicingProviders}
            loading={loading}
            paginationProps={paginationProps}
            rowHighlightColor="#F2F2FC"
            selectedKeys={providerHubContext.selectedServicingProviders || []}
            showCheckboxes={true}
            testId="provider-hub__providers-table"
          />
        </PageSection>
      </div>
      
    </>
  )
}

export default ProviderHubProvidersView;
